import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Home as HomeIcon, Heart, Brain, Star, MapPin, ArrowRight, ChevronRight, MessageSquareText, Users, Handshake, BookOpen } from 'lucide-react';
import SearchBar from '@/components/SearchBar';
import api from '@/lib/api';

const Home = () => {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [blogArticles, setBlogArticles] = useState([]);
  const [tendenciasArticles, setTendenciasArticles] = useState([]);
  const [latestEpisode, setLatestEpisode] = useState(null);
  const [convenios, setConvenios] = useState([]);

  useEffect(() => {
    api.get('/providers?featured=true').then(res => {
      const data = res.data.results || res.data;
      const sorted = Array.isArray(data) ? data.sort((a, b) => (b.rating || 0) - (a.rating || 0)) : [];
      setFeatured(sorted);
    }).catch(() => {});
    api.get('/blog/articles?limit=20').then(res => {
      const articles = Array.isArray(res.data) ? res.data : [];

      const tendencias = articles.filter(
        a => ['Editorial', 'Tendencias'].includes(a.category)
      );

      const actualidad = articles
        .filter(a => !['Editorial', 'Tendencias'].includes(a.category))
        .slice(0, 6);

      setTendenciasArticles(tendencias);
      setBlogArticles(actualidad);
    }).catch(() => {});
    

    api.get('/partners/convenios')
      .then(res => setConvenios(res.data || []))
      .catch(() => {});
api.get('/podcast/episodes').then(res => {
      const eps = res.data;
      if (Array.isArray(eps) && eps.length > 0) setLatestEpisode(eps[0]);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-[#ffff00] pt-16 pb-12 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto" data-testid="hero-section">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#000000] leading-tight mb-6">
              Encuentra y comparte lo mejor
              <span className="block text-[#000000]"></span>
              para tu mascota.
            </h1>
            <p className="text-sm text-gray-500 max-w-2xl mx-auto mb-10">
              Encuentra, lee y escribe opiniones.
            </p>
          </div>

          {/* SearchBar - ancho completo */}
          <div className="mb-8">
            <SearchBar />
          </div>
        </div>
      </section>

      {/* Servicios Destacados */}
      {featured.length > 0 && (
        <FeaturedGrid featured={featured} />
      )}

      {/* PetPodcast: solo aparece cuando existe al menos un episodio */}
      {latestEpisode && (
        <section className="py-20 bg-gray-50" data-testid="podcast-home-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Video del último episodio */}
              <div className="flex justify-center">
                <div className="relative w-full" style={{ maxWidth: '500px' }}>
                  <div className="aspect-video rounded-2xl shadow-lg overflow-hidden">
                    {latestEpisode.youtube_url ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${latestEpisode.youtube_url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([^?&\s]+)/)?.[1] || ''}`}
                        title={latestEpisode.title}
                        className="w-full h-full"
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <div className="w-full h-full bg-[#000000] flex items-center justify-center">
                        <span className="text-white text-xl font-bold">
                          PetPodcast
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Contenido */}
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <img
                    src="https://pettv.cl/logo-pettv.png"
                    alt="PetTV"
                    className="h-12"
                  />
                  <span className="text-xl md:text-2xl font-semibold text-[#000000]">
                    Canal para Mascotas
                  </span>
                </div>

                <h2 className="text-3xl md:text-4xl font-bold text-[#000000]">
                  {latestEpisode.title || 'Conversaciones sobre mascotas y bienestar animal'}
                </h2>

                {latestEpisode.description && (
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {latestEpisode.description.length > 180
                      ? `${latestEpisode.description.slice(0, 180).trim()}...`
                      : latestEpisode.description}
                  </p>
                )}

                <div className="pt-3 flex flex-wrap gap-3">
                  <Link to="/podcast">
                    <Button className="bg-[#ff00ff] hover:bg-[#e600e6] text-[#000000] font-bold px-6 py-3 rounded-xl text-sm">
                      Ver PetTV
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>

                  <a
                    href="https://pettv.cl"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="bg-[#000000] hover:bg-[#222222] text-white font-bold px-6 py-3 rounded-xl text-sm">
                      Ver Sitio Web
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Como usar PetAdvisor */}
      <section className="bg-[#ffff00]" data-testid="cta-banner">
        <div className="flex flex-col md:flex-row items-stretch">
          <div className="md:w-4/12 relative min-h-[350px] md:min-h-0">
            <img
              src="/como-usar-petadvisor.jpg"
              alt="Cómo usar PetAdvisor"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          </div>
          <div className="md:w-8/12 flex items-center px-8 py-14 md:py-16 md:px-12">
              <div className="text-[#000000] w-full">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
                  ¿Cómo usar PetAdvisor?
                </h2>
                <p className="text-lg md:text-xl leading-relaxed mb-10 text-center">
                  Encuentra, evalúa y comparte tu experiencia en simples pasos:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 rounded-full border-2 border-[#000000] bg-[#ffff00] flex items-center justify-center mb-3">
                      <MapPin className="w-9 h-9 text-[#000000]" strokeWidth={2.5} />
                    </div>
                    <p className="font-bold text-2xl md:text-3xl mb-2">Busca un servicio</p>
                    <p className="text-[#000000]/80 text-base md:text-lg leading-relaxed">Explora los distintos servicios, y ayúdanos a filtrar los mejores.</p>
                  </div>
                  <div className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 rounded-full border-2 border-[#000000] bg-[#ffff00] flex items-center justify-center mb-3">
                      <MessageSquareText className="w-9 h-9 text-[#000000]" strokeWidth={2.5} />
                    </div>
                    <p className="font-bold text-2xl md:text-3xl mb-2">Deja tu reseña</p>
                    <p className="text-[#000000]/80 text-base md:text-lg leading-relaxed">Escribe tu experiencia y otorga una valoración con estrellas.</p>
                  </div>
                  <div className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 rounded-full border-2 border-[#000000] bg-[#ffff00] flex items-center justify-center mb-3">
                      <Users className="w-9 h-9 text-[#000000]" strokeWidth={2.5} />
                    </div>
                    <p className="font-bold text-2xl md:text-3xl mb-2">Ayuda a otros</p>
                    <p className="text-[#000000]/80 text-base md:text-lg leading-relaxed">Tu opinión orienta a más personas a tomar una mejor decisión.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Video */}
            <div className="flex justify-center">
              <div className="relative w-full" style={{ maxWidth: '500px' }}>
                <div className="aspect-video rounded-2xl shadow-lg overflow-hidden">
                  <iframe
                    src="https://www.youtube.com/embed/wxwBk4FvVmk"
                    title="PetAdvisor"
                    className="w-full h-full"
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
            {/* Text Content */}
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-[#000000]">Sobre Nosotros</h2>
              <div className="space-y-6 text-gray-700 leading-relaxed text-lg">
                <p>
                  <span className="bg-[#ffff00] text-[#000000] font-bold px-2 py-1 rounded">
                    PetAdvisor
                  </span>{" "}
                  es una plataforma creada para ayudar a las familias a encontrar los mejores servicios para sus mascotas, reuniendo en un solo lugar información, recomendaciones y experiencias reales de la comunidad.
                </p>

                <p>
                  Descubre{" "}
                  <span className="bg-[#ffff00] text-[#000000] font-bold px-2 py-1 rounded">
                    veterinarias
                  </span>
                  ,{" "}
                  <span className="bg-[#ffff00] text-[#000000] font-bold px-2 py-1 rounded">
                    tiendas
                  </span>
                  ,{" "}
                  <span className="bg-[#ffff00] text-[#000000] font-bold px-2 py-1 rounded">
                    hoteles
                  </span>
                  ,{" "}
                  <span className="bg-[#ffff00] text-[#000000] font-bold px-2 py-1 rounded">
                    peluquerías
                  </span>
                  ,{" "}
                  <span className="bg-[#ffff00] text-[#000000] font-bold px-2 py-1 rounded">
                    adiestradores
                  </span>
                  ,{" "}
                  <span className="bg-[#ffff00] text-[#000000] font-bold px-2 py-1 rounded">
                    cuidadores
                  </span>{" "}
                  y muchos otros servicios, comparando opiniones y valoraciones para ayudarte a tomar la mejor decisión para tu mascota.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* PetClub - se muestra solo si existen convenios activos */}
      {convenios.length > 0 && (
      <section className="py-12 bg-white" data-testid="pet-club-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/petclub" className="block">
            <div className="bg-[#000000] rounded-2xl hover:bg-[#1a1a1a] transition-colors cursor-pointer group overflow-hidden">
              <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10 px-8 py-8 md:py-6">
                <div className="shrink-0">
                  <img
                    src="https://customer-assets.emergentagent.com/job_316c0f31-5a86-43b3-bcc3-d5c9be92d49a/artifacts/y9u1s2ae_seniorclub.svg"
                    alt="PetClub"
                    className="h-16 md:h-20"
                    style={{ filter: 'brightness(0) invert(1)' }}
                  />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-white text-xl md:text-2xl font-bold mb-1">Beneficios exclusivos para el cuidado y bienestar de tus mascotas</h3>
                  <p className="text-gray-300 text-sm">Accede a descuentos, promociones y servicios preferenciales de nuestros aliados</p>
                </div>
                <div className="shrink-0">
                  <div className="bg-[#ffff00] hover:bg-[#f2f200] text-[#000000] font-bold px-6 py-3 rounded-xl text-sm group-hover:scale-105 transition-transform flex items-center gap-2">
                    Ver Convenios <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>
      )}

      {/* TendenciasPet: solo aparece cuando existen artículos */}
      {tendenciasArticles.length > 0 && (
      <section className="py-12 bg-white" data-testid="tendencias-banner-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/tendencias" className="block">
            <div className="bg-[#ffff00] rounded-2xl hover:bg-[#f2f200] transition-colors cursor-pointer group overflow-hidden">
              <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10 px-8 py-8 md:py-6">
                <div className="shrink-0 flex items-center justify-center w-20 h-20 bg-[#000000] rounded-2xl">
                  <BookOpen className="w-10 h-10 text-[#ffff00]" strokeWidth={2.2} />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-[#000000] text-3xl md:text-4xl leading-tight">
                    <span className="font-extrabold">Tendencias</span><span className="font-bold">Pet</span>
                  </h3>
                  <p className="text-[#000000]/70 text-base">Descubre las últimas innovaciones para el mundo Pet.</p>
                </div>
                <div className="shrink-0">
                  <div className="bg-[#000000] hover:bg-[#1a1a1a] text-white font-bold px-6 py-3 rounded-xl text-sm group-hover:scale-105 transition-transform flex items-center gap-2" data-testid="tendencias-banner-cta">
                    Ver Tendencias <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>
      )}

      {/* Actualidad Pet - Blog */}
      {blogArticles.length > 0 && (
        <section className="py-16 bg-white" data-testid="blog-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10">
              <h2 className="text-4xl font-bold text-[#000000] mb-2">Actualidad Pet</h2>
              <p className="text-gray-500 text-lg">Noticias, beneficios y recomendaciones para inspirarte</p>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {blogArticles.slice(0, 6).map((article) => (
                <Link
                  key={article.slug}
                  to={`/blog/${article.slug}`}
                  className="group relative aspect-[4/3] rounded-2xl overflow-hidden"
                  data-testid={`blog-${article.slug}`}
                >
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="text-white font-bold text-lg leading-tight">{article.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/blog">
                <Button className="bg-[#000000] text-white hover:bg-[#4a5568] px-8 py-4 text-base font-bold rounded-xl">
                  Ver Toda la Actualidad <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA for Providers */}
      <section className="py-20 bg-gradient-to-r from-[#ffff00] to-[#e6e600]">
        <div className="max-w-7xl mx-auto px-4 text-center text-[#000000]">
          <h2 className="text-4xl font-bold mb-4">¿Ofreces servicios para mascotas?</h2>
          <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">Únete a PetAdvisor y conecta con miles de familias que buscan servicios de calidad como el tuyo.</p>
          <Link to="/provider/register">
            <Button className="bg-[#000000] text-white hover:bg-[#4a5568] px-12 py-6 text-lg font-bold rounded-xl">
              Registrar mi Servicio Gratis
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#000000] py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-white text-sm opacity-80">
          PetAdvisor - Todos los derechos reservados
        </div>
      </footer>
    </div>
  );
};

const FeaturedGrid = ({ featured }) => {
  const FeaturedCard = ({ p }) => (
    <Link
      to={`/provider/${p.provider_id}`}
      className="group block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow"
      data-testid={`featured-${p.provider_id}`}
    >
      <div className="h-44 bg-gray-200 overflow-hidden">
        <img
          src={p.photos?.[0] || p.gallery?.[0]?.url || ''}
          alt={p.business_name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-1 mb-1.5">
          {[1,2,3,4,5].map(s => (
            <Star key={s} className={`w-3.5 h-3.5 ${s <= Math.round(p.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
          ))}
          <span className="text-sm font-bold text-[#000000] ml-1">{p.rating?.toFixed(1)}</span>
        </div>
        <h3 className="font-bold text-[#000000] text-base mb-1 group-hover:text-[#ffff00] transition-colors">{p.business_name}</h3>
        <p className="text-sm text-gray-500 flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5" /> {p.comuna}
        </p>
      </div>
    </Link>
  );

  return (
    <section className="py-16 bg-[#ffff00]/60" data-testid="featured-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h2 className="text-4xl font-bold text-[#000000] mb-1">Servicios para Mascotas Destacados</h2>
          <p className="text-[#000000]/60 text-lg">Los servicios mejor evaluados por la comunidad PetAdvisor</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          {featured.slice(0, 6).map((p) => (
            <FeaturedCard key={p.provider_id} p={p} />
          ))}
        </div>

        <div className="text-center mt-10">
          <Link to="/destacados">
            <Button 
              className="bg-[#000000] text-[#ffff00] hover:bg-[#4a5568] px-8 py-4 text-base font-bold rounded-xl"
              data-testid="toggle-all-featured"
            >
              Ver Todos los Destacados
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Home;
